import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { PaymentConsentRequest } from '@airwallex/airwallex-payment-elements';
import { PAYMENT_CONSENT_ACTION, PAYMENT_CONSENT_STATUS } from '~redux/reducers/paymentConsent';
import { INTENT_ACTION } from '~redux/reducers/intent';
import { ERROR_ACTIONS } from '~redux/reducers/error';
import { getRequest } from '~redux/selectors';
import { clientStoreItem, getCheckoutApiUrl, getAirwayUrl, sendRequestLog } from '~components/utils';
import { POST_EVENTS, sendEvent, ExtendElementType } from '~components/events';
import { buildHeaders, checkoutInstrumentConfirmAsync, sendOnChallengeEvent } from './confirm';
import { createPaymentMethod, CreatePaymentMethodPayload } from './paymentMethod';
import { localStorageKeys } from '~components/constant';

interface CreatePaymentConsentPayload {
  paymentMethodId: string;
  customer_id: string;
  currency?: string;
  next_triggered_by?: string;
  merchant_trigger_reason?: string;
  requires_cvc?: boolean;
  componentName: ExtendElementType;
}

export function* createPaymentConsentAsync(createPaymentConsentPayload: CreatePaymentConsentPayload) {
  const {
    paymentMethodId,
    customer_id,
    currency,
    next_triggered_by,
    merchant_trigger_reason,
    requires_cvc,
    componentName,
  } = createPaymentConsentPayload;
  const request = yield select(getRequest);
  const reqPath = `${getAirwayUrl()}/payment_consents/create`;
  const reqPayload = {
    request_id: uuid(),
    customer_id,
    currency,
    payment_method: {
      id: paymentMethodId,
      type: 'card',
    },
    next_triggered_by,
    merchant_trigger_reason,
    requires_cvc,
  };
  const reqHeader = buildHeaders(request);
  sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
  const { data: paymentConsent } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
  sendEvent(
    {
      type: componentName,
      code: POST_EVENTS.onLogResponse,
      path: reqPath,
      res: paymentConsent,
      header: {
        ...reqHeader,
        'Awx-Tracker': null,
      },
    },
    request.origin,
  );
  yield put({ type: PAYMENT_CONSENT_ACTION, payload: paymentConsent });
  return paymentConsent;
}

interface VerifyPaymentConsentPayload {
  consentId: string;
  cvc?: string;
  componentName: ExtendElementType;
  bin: string;
  currency?: string;
}

export function* verifyPaymentConsentAsync(payload: VerifyPaymentConsentPayload) {
  const request = yield select(getRequest);
  const reqPath = `${getAirwayUrl()}/payment_consents/${payload.consentId}/verify`;
  const reqPayload = {
    request_id: uuid(),
    verification_options: {
      card: {
        cvc: payload.cvc,
        amount: 1,
        currency: payload.currency,
      },
    },
    return_url: `${getCheckoutApiUrl()}/elements/3ds?origin=${window.location.origin}&client_secret=${
      request.client_secret
    }&checkoutOrigin=${request.origin || window.location.origin}`,
  };
  const reqHeader = buildHeaders(request);
  sendRequestLog({ componentName: request.componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
  const { data: verifyResponse } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
  sendEvent(
    {
      type: request.componentName,
      code: POST_EVENTS.onLogResponse,
      path: reqPath,
      res: verifyResponse,
      header: {
        ...reqHeader,
        'Awx-Tracker': null,
      },
    },
    request.origin,
  );
  if (verifyResponse?.next_action) {
    clientStoreItem(localStorageKeys.INTENT_ID_OF_VERIFICATION, verifyResponse.initial_payment_intent_id);
    sendOnChallengeEvent(
      request,
      verifyResponse?.next_action,
      payload.componentName,
      payload.bin,
      verifyResponse.initial_payment_intent_id,
    );
    return verifyResponse;
  } else {
    const reqPath = `${getAirwayUrl()}/payment_intents/${verifyResponse.initial_payment_intent_id}`;
    const reqHeader = buildHeaders(request);
    sendRequestLog({ componentName: request.componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const { data: intent } = yield call([axios, 'get'], reqPath, reqHeader);
    sendEvent(
      {
        type: request.componentName,
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: intent,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    yield put({ type: INTENT_ACTION, payload: intent });
    yield put({ type: PAYMENT_CONSENT_ACTION, payload: verifyResponse });
    return verifyResponse;
  }
}

export function* createPaymentConsent(data: {
  type: string;
  payload: Omit<PaymentConsentRequest, 'element'> & CreatePaymentMethodPayload;
}) {
  yield put({ type: ERROR_ACTIONS.SET, payload: {} });
  try {
    yield put({ type: PAYMENT_CONSENT_STATUS, payload: 'PROCESSING' });
    const {
      payment_method_id,
      intent_id,
      currency,
      customer_id,
      componentName,
      next_triggered_by,
      merchant_trigger_reason,
      requires_cvc,
    } = data?.payload || {};
    let paymentMethodId = payment_method_id;
    if (!paymentMethodId) {
      paymentMethodId = yield createPaymentMethod({
        type: '',
        payload: data.payload,
      });
    }

    if (paymentMethodId) {
      const paymentConsent = yield createPaymentConsentAsync({
        paymentMethodId,
        currency,
        customer_id,
        next_triggered_by,
        merchant_trigger_reason,
        requires_cvc,
        componentName,
      });
      if (intent_id) {
        yield checkoutInstrumentConfirmAsync({
          type: '',
          payload: {
            payment_consent_id: paymentConsent.id,
            cvc: data.payload.cvc,
            id: intent_id,
            componentName,
          },
        });
      } else {
        const verifyResponse = yield verifyPaymentConsentAsync({
          consentId: paymentConsent.id,
          cvc: data.payload.cvc,
          componentName,
          currency,
          bin: data.payload.cardnumber?.slice(0, 6) || '',
        });
        yield put({ type: PAYMENT_CONSENT_ACTION, payload: verifyResponse });
        if (!verifyResponse.next_action) {
          yield put({ type: PAYMENT_CONSENT_STATUS, payload: 'DONE' });
        }
      }
    }
  } catch (error) {
    yield put({ type: PAYMENT_CONSENT_STATUS, payload: 'ERROR' });
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
  }
}

export const AWX_PAYMENT_CONSENT = 'AWX_PAYMENT_CONSENT';

export function* watchPaymentConsentAsync() {
  yield takeLatest(AWX_PAYMENT_CONSENT, createPaymentConsent);
}
